import { Component, ComponentInterface, Event, EventEmitter, h, Prop, State } from '@stencil/core';
import { UiHubPages, UiPage } from '../ui-cms-navigation';

@Component({
  tag: 'ui-cms-navigation-element',
  styleUrl: 'ui-cms-navigation-element.scss',
})
export class UiNavigationElement implements ComponentInterface {
  NavigationElement!: HTMLLIElement;

  FlyoutElement!: HTMLDivElement;

  timeoutHandlerOn: any;

  timeoutHandlerOff: any;

  timeoutValue = 200;

  dataLevel = 1;

  @Prop() page: UiPage = {} as any;

  // SET in ui-cms-header
  // allows default navigation on ui-cms-navigation-element menu item click if TRUE
  // prevents default navigation on ui-cms-navigation-element menu item click and emits triggerExternalNavigation event if FALSE
  @Prop() navigateOnClick: boolean;

  @State() menubarXPosition = 0;

  @Event() linkClick: EventEmitter<{ page: UiPage }>;

  @Event() triggerExternalNavigation: EventEmitter<{ page: UiPage }>;

  getDeeperLevel(page: UiPage, dataLevel: number) {
    let { active } = page;

    if (active && dataLevel === 2 && page?.pages) {
      active = !this.checkIsLowerLevelActive(page?.pages);
    }

    return (
      <li
        class={{
          active,
        }}
      >
        <a href={page.url} role="menuitem" data-level={dataLevel} onClick={(e) => this.onLinkClick(page, e)}>
          <strong>{page.title}</strong>
          {page.description && <span class="menu-item-meta"> {page.description}</span>}
        </a>
        {page?.pages && <ul>{page.pages?.map((singlePage) => this.getDeeperLevel(singlePage, dataLevel + 1))}</ul>}
      </li>
    );
  }

  checkIsLowerLevelActive(pages = []) {
    const activePages = pages.filter((page) => page.active === true);

    return activePages.length > 0;
  }

  getHubNavigation(hubPage: UiHubPages) {
    return (
      <li class="hub-navigation">
        <strong class="hub-navigation__headline">{hubPage.title}</strong>
        <ul>{hubPage.pages?.map((page) => this.getHubItem(page))}</ul>
      </li>
    );
  }

  getHubItem(page: UiPage) {
    return (
      <li>
        <article class="hub-link-wrapper">
          <a href={page.url} target="_blank" rel="noopener noreferrer" onClick={(e) => this.onLinkClick(page, e)}>
            <h2>{page.title}</h2>
            <p>{page.description}</p>
          </a>
        </article>
      </li>
    );
  }

  onLinkClick(page: UiPage, event?: Event) {
    event?.stopPropagation();

    this.linkClick.emit({ page });
  }

  private onMenuItemClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.navigateOnClick) {
      const pageWithoutSubpages = this.NavigationElement.classList.contains('columns0');
      if (e.type !== 'mouseenter' || pageWithoutSubpages) {
        const el = e.target.closest('a');
        if (
          pageWithoutSubpages ||
          (this.NavigationElement.classList.contains('hover') && el.getAttribute('href') !== null)
        ) {
          // eslint-disable-next-line no-restricted-globals
          location.href = el.getAttribute('href');
          this.onLinkClick(this.page, e);
        }
      } else {
        this.onMouseEnter();
      }
    } else {
      // emit event to handle navigation in external element
      this.triggerExternalNavigation.emit({ page: this.page });
    }
  }

  private onMouseEnter() {
    if (this.FlyoutElement) {
      window.clearTimeout(this.timeoutHandlerOn);

      if (this.NavigationElement.classList.contains('hover')) {
        this.timeoutValue = 160;
      }

      this.timeoutHandlerOn = window.setTimeout(() => {
        this.NavigationElement.classList.add('hover');
        this.FlyoutElement.setAttribute('aria-expanded', 'true');
        this.FlyoutElement.setAttribute('aria-hidden', 'false');
        this.setEqualHeight();
      }, this.timeoutValue);
    }
  }

  private onMouseLeave() {
    if (this.FlyoutElement) {
      window.clearTimeout(this.timeoutHandlerOn);
      this.timeoutHandlerOff = window.setTimeout(() => {
        this.NavigationElement.classList.remove('hover');
        this.FlyoutElement.setAttribute('aria-expanded', 'false');
        this.FlyoutElement.setAttribute('aria-hidden', 'true');
      }, 250);
    }
  }

  setEqualHeight() {
    // equal height to 2nd/3rd/4th row
    const flyout = this.FlyoutElement;
    const flyoutRows = parseInt(flyout.getAttribute('data-rows'), 10);
    const itemWrapper = flyout.querySelector('.menu-wrapper');
    flyout.classList.add('position-helper');
    const rowItems = itemWrapper.children;
    let i = 0;
    for (let row = 0; row < flyoutRows; row += 1) {
      let height = 0;
      const sliceStart = row * 4;
      const sliceEnd = row * 4 + 4;

      // checking for highest navigation element in relevant row
      for (i; i < (row + 1) * 4; i += 1) {
        if (rowItems[i] && !rowItems[i].classList.contains('hub-navigation')) {
          const outerHeight = rowItems[i].scrollHeight;
          if (outerHeight > height) {
            height = outerHeight;
          }
        }
      }

      // adding height to relevant navigation elements
      for (let y = sliceStart; y < sliceEnd; y += 1) {
        // height is 49px to big - because padding is included
        const adaptedHeight = height - 49;
        if (!rowItems[y]?.classList.contains('hub-navigation')) {
          rowItems[y]?.setAttribute('style', `height: ${adaptedHeight}px`);
        }
      }
    }

    flyout.classList.remove('position-helper');
  }

  componentDidLoad() {
    this.menubarXPosition = this.NavigationElement.closest('[role="menubar"]').getBoundingClientRect().left;
  }

  render() {
    const itemsInNavigation = this.page?.pages ? this.page?.pages.length : 0;
    const rows = Math.ceil(itemsInNavigation / 4);
    const liClass = {
      [`static first-level columns${itemsInNavigation}`]: true,
      active: this.page.active,
    };
    const divClass = {
      [`flyout align-center rows-${rows}`]: true,
      multilines: rows > 1,
    };
    return (
      <li
        role="presentation"
        class={liClass}
        onClick={(e) => this.onMenuItemClick(e)}
        onMouseEnter={() => this.onMouseEnter()}
        onMouseLeave={() => this.onMouseLeave()}
        ref={(el) => {
          this.NavigationElement = el as HTMLLIElement;
        }}
      >
        <a href={this.page.url} role="menuitem" data-level={this.dataLevel}>
          <strong>{this.page.title}</strong>
        </a>
        {this.page?.pages && (
          <div
            aria-expanded="false"
            aria-hidden="true"
            role="menu"
            class={divClass}
            data-columns="4"
            data-rows={rows}
            ref={(el) => {
              this.FlyoutElement = el as HTMLDivElement;
            }}
          >
            <ul class="menu-wrapper">
              {this.page?.pages.map((page) => this.getDeeperLevel(page, this.dataLevel + 1))}
              {this.page?.hubPages?.pages?.length > 0 && this.getHubNavigation(this.page.hubPages)}
            </ul>
          </div>
        )}
      </li>
    );
  }
}
